/**
 * notification_swipe_controller.js — swipe-to-dismiss notification rows (Stimulus).
 *
 * Attached to the notification dropdown list (data-controller="notification-swipe").
 * Each direct child row carrying .notification-item can be dragged horizontally;
 * past a threshold it animates off-screen and is removed, otherwise it snaps back.
 * Pointer Events cover both touch and mouse. Axis detection on the first move
 * lets vertical gestures fall through to the list's native scroll — only clearly
 * horizontal drags are treated as a swipe (and preventDefault'd).
 *
 * On removal the bell badge (data-badge-id) is updated to the remaining count and
 * hidden at zero, and an empty-state line is injected when the list empties.
 *
 * Lifecycle: connect() binds pointerdown on the list and move/up on window (so a
 * drag continues outside the row); disconnect() removes all of them.
 */

import { Controller } from "@hotwired/stimulus";
import { bump, crossedThreshold, flipSettle, lockAxis, motionToken, rubberBand } from "../../gestures.js";

    var MIN_DISMISS_PX = 80;       // absolute floor before a swipe counts as dismiss
    var DISMISS_FRACTION = 0.4;    // ...or this fraction of the row width
    var AXIS_DEADZONE = 6;         // px before committing to a horizontal/vertical axis

export default class extends Controller {
        connect() {
            this.active = null;
            this.startX = 0;
            this.startY = 0;
            this.dx = 0;
            this.axis = null;     // 'h' | 'v' | null (undecided)
            this.bumping = false; // mid overscroll-bump (no-scrollbar case)

            this._onDown = this._down.bind(this);
            this._onMove = this._move.bind(this);
            this._onUp = this._up.bind(this);

            this.element.addEventListener('pointerdown', this._onDown);
            window.addEventListener('pointermove', this._onMove, { passive: false });
            window.addEventListener('pointerup', this._onUp);
            window.addEventListener('pointercancel', this._onUp);
        }

        disconnect() {
            this.element.removeEventListener('pointerdown', this._onDown);
            window.removeEventListener('pointermove', this._onMove);
            window.removeEventListener('pointerup', this._onUp);
            window.removeEventListener('pointercancel', this._onUp);
            if (this._dismissTimer) { clearTimeout(this._dismissTimer); this._dismissTimer = null; }
            if (this._onTransitionEnd && this.active) {
                this.active.removeEventListener('transitionend', this._onTransitionEnd);
                this._onTransitionEnd = null;
            }
            this.active = null;
            this.element.style.transition = '';
            this.element.style.transform = '';
        }

        _down(e) {
            var item = e.target.closest('.notification-item');
            if (!item || !this.element.contains(item)) return;
            this.active = item;
            this.startX = e.clientX;
            this.startY = e.clientY;
            this.dx = 0;
            this.axis = null;
            item.classList.add('is-pressed');  // tap-feedback highlight
        }

        _move(e) {
            if (!this.active) return;
            var dx = e.clientX - this.startX;
            var dy = e.clientY - this.startY;

            if (this.axis === null) {
                var axis = lockAxis(dx, dy, AXIS_DEADZONE);
                if (axis === null) return;                   // still inside the deadzone
                this.axis = axis;
                this.active.classList.remove('is-pressed');  // movement → not a tap
                if (this.axis === 'h') this.active.classList.add('is-dragging');
            }
            if (this.axis === 'v') {
                // When the list actually scrolls, leave native scroll + its own edge
                // effect alone. Only when there's no scrollbar (few notifications) do
                // we fake a damped rubber-band so the area still feels responsive.
                var el = this.element;
                if (el.scrollHeight > el.clientHeight + 1) return;  // has scroll → native
                e.preventDefault();
                if (!this.bumping) { this.bumping = true; el.style.transition = 'none'; }
                var damped = rubberBand(dy, 140, 0.6);       // diminishing pull
                el.style.transform = 'translateY(' + damped + 'px)';
                return;
            }
            if (this.axis !== 'h') return;

            e.preventDefault();
            this.dx = dx;
            var width = this.active.offsetWidth || 1;
            this.active.style.transform = 'translateX(' + dx + 'px)';
            this.active.style.opacity = String(Math.max(0, 1 - Math.abs(dx) / width));
        }

        _up() {
            if (!this.active) return;
            var item = this.active;
            var axis = this.axis;
            var dx = this.dx;
            this.active = null;
            this.axis = null;
            if (this.bumping) {                              // spring the overscroll back
                this.bumping = false;
                var el = this.element;
                el.style.transition = 'transform 0.28s ' + motionToken('--ease-settle');
                el.style.transform = '';
                setTimeout(function () { el.style.transition = ''; }, 300);
            }
            if (axis === null) {                             // pure tap, no drag
                bump();
                setTimeout(function () { item.classList.remove('is-pressed'); }, 130);
                return;
            }
            item.classList.remove('is-pressed');
            if (axis !== 'h') return;                        // vertical scroll

            item.classList.remove('is-dragging');
            var width = item.offsetWidth || 1;
            if (crossedThreshold(dx, MIN_DISMISS_PX, DISMISS_FRACTION, width)) {
                this._dismiss(item, dx < 0 ? -1 : 1, width);
            } else {
                item.style.transform = '';
                item.style.opacity = '';
            }
        }

        _dismiss(item, dir, width) {
            // Persist through the shared notification seam (NotificationUI) so the
            // swiped row does not reappear on the next fetch. The fling + FLIP below
            // stay purely local; this is the only server-side hook.
            var id = item.dataset.id;
            if (id && window.NotificationUI && window.NotificationUI.persistDismiss) {
                // Pass the row's source surface so the unified bell dismisses
                // against the right API (business vs per-user); absent = primary.
                window.NotificationUI.persistDismiss(id, item.dataset.base);
            }
            // Phase 1: fling the row off-screen horizontally (CSS transition on
            // .notification-item animates transform/opacity).
            var self = this;
            var slid = false;
            var slide = this._onTransitionEnd = () => {
                if (slid) return;
                slid = true;
                self._onTransitionEnd = null;
                self._dismissTimer = null;
                item.removeEventListener('transitionend', slide);
                self._slideUp(item);
            };
            item.style.transform = 'translateX(' + (dir * (width + 40)) + 'px)';
            item.style.opacity = '0';
            item.addEventListener('transitionend', slide);
            this._dismissTimer = setTimeout(slide, 260);  // fallback if transitionend is dropped
        }

        _slideUp(item) {
            // Phase 2: remove the flung row instantly, then FLIP the rows below it
            // up into the freed space. Animating transform (compositor) instead of
            // height (per-frame layout) keeps the slide at 60fps.
            var followers = [];
            var n = item.nextElementSibling;
            while (n) {
                if (n.classList && n.classList.contains('notification-item')) followers.push(n);
                n = n.nextElementSibling;
            }
            var shift = item.offsetHeight;  // vertical space the row occupied
            item.remove();
            this._refresh();
            if (!followers.length || !shift) return;

            // Every follower moved by exactly the removed row's height, so the
            // deltas are uniform here (the entity list measures per-row rects
            // instead — same inversion, different measurement).
            flipSettle(followers.map(function (f) { return { el: f, delta: shift }; }), 180);
        }

        _refresh() {
            var count = this.element.querySelectorAll('.notification-item').length;

            var badgeId = this.element.dataset.badgeId;
            if (badgeId) {
                var badge = document.getElementById(badgeId);
                if (badge) {
                    badge.textContent = String(count);
                    badge.classList.toggle('d-none', count === 0);
                }
            }

            if (count === 0 && !this.element.querySelector('.notification-empty-state')) {
                var empty = document.createElement('div');
                empty.className = 'text-center text-muted py-4 small notification-empty-state';
                empty.textContent = 'No notifications';
                this.element.appendChild(empty);
            }
        }
}
